// biome-ignore-all lint/a11y/noNoninteractiveTabindex: focusable severity terms reveal their associated definition tooltips without implying an action.
// biome-ignore-all lint/a11y/noStaticElementInteractions: severity terms handle Escape solely to dismiss their tooltip and are intentionally not buttons.

"use client";

/**
 * One point in a version card's timeline. Two variants:
 * - assumption edit: teal badge, label, strikethrough old → bold new, and a
 *   kebab menu with "Open in assumptions".
 * - revision: amber badge (red-tinted when any item is major) plus a list of
 *   items tagged Major/Minor with definition tooltips.
 * Ported from the design's `.a-row` / `.badge` / `.revitem` classes.
 */

import { useEffect, useId, useRef, useState } from "react";
import type { HistoryPointVM, HistorySeverity } from "./history-types";
import { SEVERITY_TOOLTIPS } from "./history-types";

export interface HistoryPointRowProps {
  point: HistoryPointVM;
  /** Author display name (the version's saver). */
  actorName: string;
  /** Preformatted time label, e.g. "9:05 AM". */
  timeLabel: string;
  /** Wires the kebab's "Open in assumptions" action. Omit to hide the kebab. */
  onOpenAssumptions?: () => void;
}

function DotLeader({ color }: { color: string }) {
  return (
    <span
      aria-hidden
      className={[
        "absolute top-[14px] -left-[24px] h-[9px] w-[9px] rounded-full border-2 bg-(--bg-white)",
        color,
      ].join(" ")}
    />
  );
}

function SeverityLabel({ severity }: { severity: HistorySeverity }) {
  const tooltipId = useId();
  const [dismissed, setDismissed] = useState(false);

  return (
    <span className="group relative mr-1 inline-block">
      <span
        tabIndex={0}
        aria-describedby={tooltipId}
        onBlur={() => setDismissed(false)}
        onFocus={() => setDismissed(false)}
        onKeyDown={(event) => {
          if (event.key !== "Escape") return;
          event.preventDefault();
          event.stopPropagation();
          setDismissed(true);
        }}
        className={[
          "cursor-help rounded-sm font-bold",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--fg-primary)",
          severity === "major"
            ? "text-(--accent-plum)"
            : "text-(--fg-secondary)",
        ].join(" ")}
      >
        {severity === "major" ? "Major" : "Minor"}
      </span>
      <span
        id={tooltipId}
        role="tooltip"
        className={[
          "pointer-events-none invisible absolute bottom-[calc(100%+6px)] left-0 z-20 w-72",
          "max-w-[calc(100vw-2rem)] rounded-md bg-(--brand-navy) px-2.5 py-1.5",
          "text-[11px] leading-snug font-normal text-(--fg-inverse) opacity-0 shadow-lg",
          dismissed
            ? ""
            : "group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100",
          "transition-[opacity,visibility]",
        ].join(" ")}
      >
        {SEVERITY_TOOLTIPS[severity]}
      </span>
    </span>
  );
}

export function HistoryPointRow({
  point,
  actorName,
  timeLabel,
  onOpenAssumptions,
}: HistoryPointRowProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuId = useId();
  const menuRef = useRef<HTMLSpanElement | null>(null);
  const menuItemRef = useRef<HTMLButtonElement | null>(null);
  const menuTriggerRef = useRef<HTMLButtonElement | null>(null);
  const menuAvailable =
    point.kind === "assumption" && onOpenAssumptions !== undefined;
  const activeMenuOpen = menuAvailable && menuOpen;

  useEffect(() => {
    if (!menuAvailable) {
      if (menuOpen) setMenuOpen(false);
      return;
    }
    if (!menuOpen) return;

    menuItemRef.current?.focus();

    function closeOnOutsidePointer(event: MouseEvent) {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
    }

    function closeOnKeyboardExit(event: KeyboardEvent) {
      if (event.key === "Tab") {
        setMenuOpen(false);
        return;
      }
      if (event.key !== "Escape") return;
      event.preventDefault();
      setMenuOpen(false);
      menuTriggerRef.current?.focus();
    }

    document.addEventListener("mousedown", closeOnOutsidePointer);
    document.addEventListener("keydown", closeOnKeyboardExit);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsidePointer);
      document.removeEventListener("keydown", closeOnKeyboardExit);
    };
  }, [menuAvailable, menuOpen]);

  const meta = (
    <span className="ml-auto shrink-0 text-[12px] whitespace-nowrap text-(--fg-secondary)">
      {actorName} <span className="text-(--fg-muted)">· {timeLabel}</span>
    </span>
  );

  if (point.kind === "assumption") {
    return (
      <div className="relative border-b border-(--border-subtle) py-2.5 last:border-b-0">
        <DotLeader color="border-(--accent-teal)" />
        <div className="flex flex-wrap items-baseline justify-between gap-x-3.5 gap-y-1">
          <span className="min-w-0 flex-1 break-words text-[13.5px] font-medium text-(--fg-primary)">
            <span className="mr-1.5 inline-block rounded-[4px] bg-(--accent-teal-bg) px-1.5 py-0.5 align-middle text-[10px] font-bold tracking-wide text-(--accent-teal) uppercase">
              Assumption edit
            </span>
            {point.label}{" "}
            {point.from !== null || point.to !== null ? (
              <span className="text-[12.5px]">
                {point.from !== null ? (
                  <span className="text-(--fg-subtle) line-through">
                    {point.from}
                  </span>
                ) : null}
                {point.from !== null && point.to !== null ? " → " : null}
                {point.to !== null ? (
                  <span className="font-bold text-(--fg-primary)">
                    {point.to}
                  </span>
                ) : null}
              </span>
            ) : null}
            {onOpenAssumptions ? (
              <span className="relative inline-block" ref={menuRef}>
                <button
                  ref={menuTriggerRef}
                  type="button"
                  aria-label={`More actions for ${point.label}`}
                  aria-controls={activeMenuOpen ? menuId : undefined}
                  aria-expanded={activeMenuOpen}
                  aria-haspopup="menu"
                  className={[
                    "cursor-pointer rounded border-0 bg-transparent px-1.5 text-[15px] leading-none text-(--fg-muted)",
                    "hover:bg-(--bg-canvas) hover:text-(--fg-primary)",
                    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--fg-primary)",
                  ].join(" ")}
                  onClick={() => setMenuOpen((value) => !value)}
                  onKeyDown={(event) => {
                    if (event.key !== "ArrowDown" && event.key !== "ArrowUp")
                      return;
                    event.preventDefault();
                    setMenuOpen(true);
                  }}
                >
                  ⋮
                </button>
                {activeMenuOpen ? (
                  <span
                    id={menuId}
                    className="absolute top-[calc(100%+4px)] left-0 z-10 flex min-w-[176px] flex-col rounded-lg border border-(--border-subtle) bg-(--bg-white) p-1 shadow-lg"
                    role="menu"
                  >
                    <button
                      ref={menuItemRef}
                      type="button"
                      role="menuitem"
                      className={[
                        "cursor-pointer rounded-md border-0 bg-transparent px-2.5 py-2",
                        "text-left text-[12.5px] font-semibold text-(--fg-primary)",
                        "hover:bg-(--bg-sky)",
                        "focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-(--fg-primary)",
                      ].join(" ")}
                      onClick={() => {
                        setMenuOpen(false);
                        onOpenAssumptions();
                      }}
                    >
                      Open in assumptions
                    </button>
                  </span>
                ) : null}
              </span>
            ) : null}
          </span>
          {meta}
        </div>
        {point.reason ? (
          <p className="mt-1.5 mb-0 rounded-(--radius-field) bg-(--bg-canvas) px-3 py-2 text-[12px] leading-[1.45] text-(--fg-secondary)">
            <span className="font-semibold">Reason:</span> {point.reason}
          </p>
        ) : null}
      </div>
    );
  }

  const hasMajor = point.items.some((item) => item.severity === "major");

  return (
    <div className="relative border-b border-(--border-subtle) py-2.5 last:border-b-0">
      <DotLeader
        color={hasMajor ? "border-(--empty)" : "border-(--accent-gold)"}
      />
      <div className="flex flex-wrap items-baseline justify-between gap-x-3.5 gap-y-1">
        <span
          className={[
            "inline-block rounded-[4px] px-1.5 py-0.5 text-[10px] font-bold tracking-wide uppercase",
            hasMajor
              ? "bg-(--empty-bg) text-(--empty)"
              : "bg-(--accent-gold-bg) text-(--accent-gold)",
          ].join(" ")}
        >
          Revision
        </span>
        {meta}
      </div>
      {point.items.length > 0 ? (
        <ul
          className="mt-1.5 flex flex-col gap-1.5"
          aria-label="Revision details"
        >
          {point.items.map((item, index) => (
            <li
              key={`${item.severity}-${item.text}-${index}`}
              className="list-none border-l-2 border-(--border-subtle) pl-3 text-[13px] leading-snug break-words text-(--fg-secondary)"
            >
              <SeverityLabel severity={item.severity} />
              {item.text}
              {item.reason ? (
                <p className="mt-1.5 mb-0 rounded-(--radius-field) bg-(--bg-canvas) px-3 py-2 text-[12px] leading-[1.45] text-(--fg-secondary)">
                  <span className="font-semibold">Reason:</span> {item.reason}
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
